import React, { useState } from 'react';
import { usePricing } from '../contexts/PricingContext';
import { useTournament } from '../contexts/TournamentContext';

export const OrderSection = ({ 
  selectedStrike, 
  selectedType, 
  quantity, 
  onQuantityChange,
  btcPrice,
  onTrade
}) => {
  const { contractSize } = usePricing();
  const { selectedCompetition, recordTrade } = useTournament();
  const [orderSide, setOrderSide] = useState('buy');

  const crashProofCurrency = (value) => {
    try {
      if (value === null || value === undefined || value === '' || isNaN(value)) {
        return '$0.00';
      }
      const num = Number(value);
      if (!isFinite(num)) return '$0.00';
      return `$${num.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      })}`;
    } catch (error) {
      return '$0.00';
    }
  };

  if (!selectedStrike) {
    return (
      <div className="bg-atticus-navy-900 rounded-xl p-6">
        <div className="text-center text-gray-400">
          Select a strike price to view order details
        </div>
      </div>
    );
  }

  const option = selectedStrike.options?.find(opt => opt.type === selectedType) ||
                 (selectedType === 'call' ? selectedStrike.call : selectedStrike.put) ||
                 (selectedStrike.premium ? { type: selectedType, premium: selectedStrike.premium } : null);
  
  if (!option) {
    return (
      <div className="bg-atticus-navy-900 rounded-xl p-6">
        <div className="text-center text-gray-400">
          No {selectedType} option available for this strike
        </div>
      </div>
    );
  }

  const premium = option.premium || 0;
  const totalPremium = premium * (quantity || 1);
  const safeStrikePrice = selectedStrike.price || 0;
  const safeBtcPrice = btcPrice || 0;
  
  // FIXED: Correct profit/loss calculations based on buy/sell
  const calculateBreakEven = () => {
    try {
      if (orderSide === 'buy') {
        // Buyer break-even
        if (selectedType === 'call') {
          return safeStrikePrice + premium;
        } else {
          return safeStrikePrice - premium;
        }
      } else {
        // Seller break-even (same as buyer but they profit below/above)
        if (selectedType === 'call') {
          return safeStrikePrice + premium;
        } else {
          return safeStrikePrice - premium;
        }
      }
    } catch (error) {
      console.warn('Break-even calculation error:', error);
      return 0;
    }
  };

  const maxProfit = orderSide === 'sell' ? premium : 'Unlimited';
  const maxLoss = orderSide === 'buy' ? premium : 'Unlimited';
  const breakEven = calculateBreakEven();

  const handleTrade = async () => {
    console.log('🎯 TRADE BUTTON CLICKED - calling onTrade prop');
    console.log('🎯 onTrade function exists:', !!onTrade);
    console.log('🎯 onTrade function:', onTrade);
    try {
      // Call the original onTrade handler
      if (onTrade) {
        console.log('🎯 Calling onTrade with data...');
        onTrade({
        strike: safeStrikePrice,
        type: selectedType,
        side: orderSide,
        quantity: quantity || 1,
        premium: premium,
        symbol: option.symbol || `BTC-${safeStrikePrice}-${selectedType.toUpperCase()}`,
        expiry: option.expiry || new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(),
        expiryHours: option.expiryHours || 4
        });
        console.log('🎯 onTrade call completed');
      } else {
        console.log('❌ onTrade function is not defined!');
      }

      // If in a tournament, record the trade
      if (selectedCompetition) {
        const tradeData = {
          option_type: selectedType,
          strike_price: safeStrikePrice,
          premium: premium,
          quantity: quantity || 1,
          side: orderSide,
          btc_price: safeBtcPrice,
          expiry_time: Math.floor(Date.now() / 1000) + (4 * 60 * 60) // 4 hours from now
        };
        
        const result = await recordTrade(tradeData);
        if (result.success) {
          console.log('✅ Trade recorded for tournament');
        } else {
          console.warn('⚠️ Failed to record tournament trade:', result.error);
        }
      }
    } catch (error) {
      console.warn('Trade execution error:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Buy/Sell Toggle */}
      <div>
        <h3 className="text-gray-400 text-lg mb-2">Order Type</h3>
        <div className="flex bg-atticus-navy-800 rounded-lg p-1">
          <button
            onClick={() => setOrderSide('buy')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              orderSide === 'buy'
                ? 'bg-green-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            BUY
          </button>
          <button
            onClick={() => setOrderSide('sell')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              orderSide === 'sell'
                ? 'bg-red-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            SELL
          </button>
        </div>
      </div>

      {/* Contract Information - WITH PLUS/MINUS BUTTONS */}
      <div>
        <h3 className="text-gray-400 text-lg mb-2">Contracts</h3>
        <div className="flex items-center justify-between bg-atticus-navy-800 rounded-lg px-4 py-3">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => onQuantityChange && onQuantityChange(Math.max(1, (quantity || 1) - 1))}
              className="w-8 h-8 bg-gray-700 hover:bg-gray-600 text-white rounded flex items-center justify-center font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={quantity <= 1}
            >
              −
            </button>
            
            <input
              type="number"
              value={quantity || 1}
              onChange={(e) => onQuantityChange && onQuantityChange(parseInt(e.target.value) || 1)}
              className="bg-transparent text-white text-2xl font-bold w-16 text-center outline-none"
              min="1"
            />
            
            <button
              onClick={() => onQuantityChange && onQuantityChange((quantity || 1) + 1)}
              className="w-8 h-8 bg-gray-700 hover:bg-gray-600 text-white rounded flex items-center justify-center font-bold transition-colors"
            >
              +
            </button>
          </div>
          
          <span className="text-gray-400">contracts</span>
        </div>
        <div className="text-gray-500 text-xs mt-1">
          Contract size: {contractSize} BTC • Notional: {crashProofCurrency(contractSize * safeBtcPrice)}
        </div>
      </div>

      {/* Order Summary */}
      <div>
        <h3 className="text-gray-400 text-lg mb-2">Order Summary</h3>
        <div className="bg-atticus-navy-800 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-400">Premium per contract:</span>
            <span className="text-white">{crashProofCurrency(premium)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Total premium:</span>
            <span className="text-white font-bold">{crashProofCurrency(totalPremium)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Break-even:</span>
            <span className="text-white">{crashProofCurrency(breakEven)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Max profit:</span>
            <span className="text-green-400">{typeof maxProfit === 'number' ? crashProofCurrency(maxProfit) : maxProfit}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Max loss:</span>
            <span className="text-red-400">{typeof maxLoss === 'number' ? crashProofCurrency(maxLoss) : maxLoss}</span>
          </div>
        </div>
      </div>

      {/* Execute Trade Button */}
      <button
        onClick={handleTrade}
        className={`w-full py-4 rounded-lg font-bold text-lg transition-colors ${
          orderSide === 'buy'
            ? 'bg-green-600 text-white hover:bg-green-700'
            : 'bg-red-600 text-white hover:bg-red-700'
        }`}
      >
        {orderSide.toUpperCase()} {quantity || 1} Contract{(quantity || 1) > 1 ? 's' : ''}
      </button>
    </div>
  );
};
